import { Box, Grid2 } from '@mui/material';
import CustomAvatar from '../lib/ui-components/Avatar/CustomAvatar';
import CustomDivider from '../lib/ui-components/Divider/CustomDivider';
import CustomTypography from '../lib/ui-components/Typography/CustomTypography';
import RightArrow from '../../assets/rightarrow.svg';
import type {
  ConsultHistoryProps,
  ConsultCanceled,
  ConsultCompleted,
  ConsultStatus,
  ConsultOnHold,
  ConsultOtherActions,
} from './ConsultHistoryService';
import React from 'react';
import '../lib/utils/utilities.css';
import {
  formatFieldValue,
  formatNameToCamelCase,
} from '../../reusableFunctions/CommonFunctions';
import './ConsultHistory.css';

const ConsultHistory = ({
  consultHistory,
  isLoading = false,
}: {
  consultHistory: ConsultHistoryProps;
  isLoading: boolean;
}) => {
  const getInitials = (fullName: string): string => {
    if (!fullName) return '';

    const words = fullName.trim().split(' ').filter(Boolean);

    if (words.length === 0) return '';

    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    const first = words[0];
    const last = words.at(-1);

    return `${first?.[0]?.toUpperCase() ?? ''}${last?.[0]?.toUpperCase() ?? ''}`;
  };

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  const renderConsultCanceled = (canceled?: ConsultCanceled) => {
    if (!canceled) {
      if (!consultHistory?.consultCanceled?.length) return null;
      canceled = consultHistory.consultCanceled[0];
    }

    return (
      <>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Box className="flex">
            <Box>
              <CustomAvatar id="user-avatar" alt="VA" width={40} height={40}>
                {getInitials(canceled.modifiedBy || '')}
              </CustomAvatar>
            </Box>

            <Box className="ml-12 text-align-left">
              <Box className="flex mb-4">
                <CustomTypography id="name" variant="h6" className="pr-4">
                  {formatNameToCamelCase(canceled.modifiedBy) || ''},
                </CustomTypography>

                <CustomTypography
                  id="cancel-consult-action"
                  variant="h6"
                  color="textDisabled"
                >
                  {canceled.action || ''}
                </CustomTypography>
              </Box>

              <Box className="flex">
                <CustomTypography
                  id="cancel-reason-label"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  Cancel Reason:
                </CustomTypography>

                <CustomTypography
                  id="cancel-reason-value"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {canceled.cancelReason || 'N/A'}
                </CustomTypography>
              </Box>

              {canceled.subCancelReason && (
                <Box className="flex">
                  <CustomTypography
                    id="tech-difficulty-label"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    Sub Reason:
                  </CustomTypography>

                  <CustomTypography
                    id="tech-difficulty-value"
                    variant="body3"
                    color="textDisabled"
                    className="pr-12"
                  >
                    {canceled.subCancelReason || 'N/A'}
                  </CustomTypography>
                </Box>
              )}

              {canceled.otherCancelReason && (
                <Box className="flex">
                  <CustomTypography
                    id="name"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    Other Reason:
                  </CustomTypography>

                  <CustomTypography
                    id="consultation"
                    variant="body3"
                    color="textDisabled"
                    className="pr-12"
                  >
                    {canceled.otherCancelReason}
                  </CustomTypography>
                </Box>
              )}

              <Box className="flex align-items-center mt-6">
                <Box className="portal-name">{canceled.portal || ''}</Box>
                <CustomTypography
                  id="date"
                  variant="body3"
                  color="textDisabled"
                >
                  {formatFieldValue(canceled.modifiedOn)}
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </Grid2>

        <Grid2
          className="mt-16 mb-16"
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <CustomDivider />
        </Grid2>
      </>
    );
  };

  const renderConsultOnHold = (onHold?: ConsultOnHold) => {
    if (!onHold) {
      if (!consultHistory?.consultOnHold?.length) return null;
      onHold = consultHistory.consultOnHold[0];
    }

    return (
      <>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Box className="flex">
            <Box>
              <CustomAvatar id="user-avatar" alt="VA" width={40} height={40}>
                {getInitials(onHold.modifiedBy || '')}
              </CustomAvatar>
            </Box>

            <Box className="ml-12 text-align-left">
              <Box className="flex mb-4">
                <CustomTypography id="name" variant="h6" className="pr-4">
                  {formatNameToCamelCase(onHold.modifiedBy) || ''},
                </CustomTypography>

                <CustomTypography
                  id="cancel-consult-action"
                  variant="h6"
                  color="textDisabled"
                >
                  {onHold.action || ''}
                </CustomTypography>
              </Box>

              <Box className="flex">
                <CustomTypography
                  id="cancel-reason-label"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  OnHold Reason:
                </CustomTypography>

                <CustomTypography
                  id="cancel-reason-value"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {onHold.onHoldReason || 'N/A'}
                </CustomTypography>
              </Box>

              {onHold.onHoldSubReason && (
                <Box className="flex">
                  <CustomTypography
                    id="tech-difficulty-label"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    Sub Reason:
                  </CustomTypography>

                  <CustomTypography
                    id="tech-difficulty-value"
                    variant="body3"
                    color="textDisabled"
                    className="pr-12"
                  >
                    {onHold.onHoldSubReason || 'N/A'}
                  </CustomTypography>
                </Box>
              )}

              {onHold.onHoldOtherReason && (
                <Box className="flex">
                  <CustomTypography
                    id="name"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    Other Reason:
                  </CustomTypography>

                  <CustomTypography
                    id="consultation"
                    variant="body3"
                    color="textDisabled"
                    className="pr-12"
                  >
                    {onHold.onHoldOtherReason}
                  </CustomTypography>
                </Box>
              )}

              <Box className="flex align-items-center mt-6">
                <Box className="portal-name">{onHold.portal || ''}</Box>
                <CustomTypography
                  id="date"
                  variant="body3"
                  color="textDisabled"
                >
                  {formatFieldValue(onHold.modifiedOn)}
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </Grid2>

        <Grid2
          className="mt-16 mb-16"
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <CustomDivider />
        </Grid2>
      </>
    );
  };

  const renderConsultCompleted = (completed?: ConsultCompleted) => {
    if (!completed) {
      if (!consultHistory?.consultCompleted?.length) return null;
      completed = consultHistory.consultCompleted[0];
    }

    return (
      <>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Box className="flex">
            <Box>
              <CustomAvatar id="user-avatar" alt="VA" width={40} height={40}>
                {getInitials(completed.completedBy || '')}
              </CustomAvatar>
            </Box>

            <Box className="ml-12 text-align-left">
              <Box className="flex mb-4">
                <CustomTypography id="name" variant="h6" className="pr-4">
                  {formatNameToCamelCase(completed.completedBy) || ''},
                </CustomTypography>

                <CustomTypography
                  id="consult-action"
                  variant="h6"
                  color="textDisabled"
                >
                  {completed.action || ''}
                </CustomTypography>
              </Box>

              <Box className="flex">
                <CustomTypography
                  id="billable-title"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  Billable:
                </CustomTypography>

                <CustomTypography
                  id="billable"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {completed.billable ? 'Yes' : 'No'}
                </CustomTypography>
              </Box>

              {completed.billable && (
                <>
                  <Box className="flex">
                    <CustomTypography
                      id="diagnosis-title"
                      color="primary"
                      variant="body3"
                      className="pr-4"
                    >
                      Diagnosis:
                    </CustomTypography>

                    <CustomTypography
                      id="diagnosis"
                      variant="body3"
                      color="textDisabled"
                      className="pr-12"
                    >
                      {completed.diagnosis || 'N/A'}
                    </CustomTypography>
                  </Box>

                  <Box className="flex">
                    <CustomTypography
                      id="em-codes-title"
                      color="primary"
                      variant="body3"
                      className="pr-4"
                    >
                      EM/Codes:
                    </CustomTypography>

                    <CustomTypography
                      id="em-codes"
                      variant="body3"
                      color="textDisabled"
                      className="pr-12"
                    >
                      {completed.emCodes || 'N/A'}
                    </CustomTypography>
                  </Box>

                  <Box className="flex">
                    <CustomTypography
                      id="recommendations-title"
                      color="primary"
                      variant="body3"
                      className="pr-4"
                    >
                      Recommendations:
                    </CustomTypography>

                    <CustomTypography
                      id="recommendations"
                      variant="body3"
                      color="textDisabled"
                      className="pr-12"
                    >
                      {completed.recommendations || 'N/A'}
                    </CustomTypography>
                  </Box>
                </>
              )}

              <Box className="flex">
                <CustomTypography
                  id="ed-ip-title"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  ED/IP:
                </CustomTypography>

                <CustomTypography
                  id="ed-ip"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {completed.unitOrDepartment || 'N/A'}
                </CustomTypography>
              </Box>
              <Box className="flex">
                <CustomTypography
                  id="follow-up-title"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  Follow-Up:
                </CustomTypography>

                <CustomTypography
                  id="follow-up"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {completed.followUp || 'N/A'}
                </CustomTypography>
              </Box>
              <Box className="flex">
                <CustomTypography
                  id="surge-title"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  Surge:
                </CustomTypography>

                <CustomTypography
                  id="surge"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {completed.surge === null || completed.surge === false
                    ? 'No'
                    : 'Yes'}
                </CustomTypography>
              </Box>
              <Box className="flex">
                <CustomTypography
                  id="comments-title"
                  color="primary"
                  variant="body3"
                  className="pr-4"
                >
                  Comments:
                </CustomTypography>

                <CustomTypography
                  id="comments"
                  variant="body3"
                  color="textDisabled"
                  className="pr-12"
                >
                  {completed.opsReasonForCompletion || 'N/A'}
                </CustomTypography>
              </Box>

              <Box className="flex align-items-center mt-6">
                <Box className="portal-name">{completed.portal || ''}</Box>
                <CustomTypography
                  id="date"
                  variant="body3"
                  color="textDisabled"
                >
                  {formatFieldValue(completed.completedOn)}
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </Grid2>

        <Grid2
          className="mt-16 mb-16"
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <CustomDivider />
        </Grid2>
      </>
    );
  };

  const getFieldLabel = (fieldName: string) => {
    if (fieldName.toLowerCase() === 'sessionstatusid') {
      return 'Consult Status';
    }
    return fieldName;
  };

  const renderConsultCreated = () => {
    if (!consultHistory) return null;

    return (
      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Box className="flex">
          <Box>
            <CustomAvatar id="user-avatar" alt="US" width={40} height={40}>
              {getInitials(consultHistory.createdBy || '')}
            </CustomAvatar>
          </Box>

          <Box className="ml-12 text-align-left">
            <Box className="flex mb-4">
              <CustomTypography id="name" variant="h6" className="pr-4">
                {formatNameToCamelCase(consultHistory.createdBy) || ''},
              </CustomTypography>

              <CustomTypography
                id="consultation"
                variant="h6"
                color="textDisabled"
                className="pr-12"
              >
                created a consult
              </CustomTypography>
            </Box>

            <Box className="flex">
              <CustomTypography
                id="name"
                variant="body3"
                color="primary"
                className="pr-4"
              >
                Session ID:
              </CustomTypography>

              <CustomTypography
                id="consultation"
                variant="body3"
                color="textDisabled"
              >
                {consultHistory.sessionId}
              </CustomTypography>
            </Box>

            <Box className="flex">
              <CustomTypography
                id="name"
                variant="body3"
                color="primary"
                className="pr-4"
              >
                Consult Status:
              </CustomTypography>

              <CustomTypography
                id="consultation"
                variant="body3"
                color="textDisabled"
              >
                {consultHistory.status}
              </CustomTypography>
            </Box>

            <Box className="flex align-items-center mt-6">
              <Box className="portal-name">
                {consultHistory.modifiedIn || ''}
              </Box>

              <CustomTypography id="date" variant="body3" color="textDisabled">
                {formatFieldValue(consultHistory.createdOn)}
              </CustomTypography>
            </Box>
          </Box>
        </Box>
      </Grid2>
    );
  };

  // Consult other actions
  const renderConsultOtherActions = (otherAction: ConsultOtherActions) => {
    return (
      <>
        <Grid2
          key={otherAction.modifiedOn}
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <Box className="flex">
            <Box>
              <CustomAvatar id="user-avatar" alt="US" width={40} height={40}>
                {getInitials(otherAction.modifiedBy)}
              </CustomAvatar>
            </Box>

            <Box className="ml-12 text-align-left">
              <Box className="flex mb-4">
                <CustomTypography id="name" variant="h6" className="pr-4">
                  {formatNameToCamelCase(otherAction.modifiedBy) || ''},
                </CustomTypography>

                <CustomTypography
                  id="consultation"
                  variant="h6"
                  color="textDisabled"
                  className="pr-12"
                >
                  {otherAction.action || ''}
                </CustomTypography>
              </Box>

              <Box className="flex align-items-center mt-6">
                <Box className="portal-name">{otherAction.portal || ''}</Box>

                <CustomTypography
                  id="date"
                  variant="body3"
                  color="textDisabled"
                >
                  {formatFieldValue(otherAction.modifiedOn)}
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </Grid2>
        <Grid2
          className="mt-16 mb-16"
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <CustomDivider />
        </Grid2>
      </>
    );
  };
  // Merge all history items into a single array with timestamps for chronological sorting
  type HistoryItem =
    | {
        type: 'canceled';
        timestamp: string;
        data: ConsultCanceled;
      }
    | {
        type: 'completed';
        timestamp: string;
        data: ConsultCompleted;
      }
    | {
        type: 'others';
        timestamp: string;
        data: ConsultOtherActions;
      }
    | {
        type: 'onHold';
        timestamp: string;
        data: ConsultOnHold;
      }
    | {
        type: 'status';
        timestamp: string;
        data: ConsultStatus;
      }
    | {
        type: 'created';
        timestamp: string;
        data: ConsultHistoryProps;
      };

  const getAllHistoryItems = (): HistoryItem[] => {
    if (!consultHistory) return [];

    const items: HistoryItem[] = [];

    // Add canceled items
    if (consultHistory.consultCanceled?.length) {
      consultHistory.consultCanceled.forEach((canceled) => {
        items.push({
          type: 'canceled',
          timestamp: canceled.modifiedOn,
          data: canceled,
        });
      });
    }

    // Add completed items
    if (consultHistory.consultCompleted?.length) {
      consultHistory.consultCompleted.forEach((completed) => {
        items.push({
          type: 'completed',
          timestamp: completed.completedOn,
          data: completed,
        });
      });
    }

    // Add status update items
    if (consultHistory.consultStatus?.length) {
      consultHistory.consultStatus.forEach((status) => {
        items.push({
          type: 'status',
          timestamp: status.modifiedOn,
          data: status,
        });
      });
    }

    if (consultHistory.consultOtherActions?.length) {
      consultHistory.consultOtherActions.forEach((otherAction) => {
        items.push({
          type: 'others',
          timestamp: otherAction.modifiedOn,
          data: otherAction,
        });
      });
    }

    if (consultHistory.consultOnHold?.length) {
      consultHistory.consultOnHold.forEach((onHold) => {
        items.push({
          type: 'onHold',
          timestamp: onHold.modifiedOn,
          data: onHold,
        });
      });
    }

    // Add created item (always last chronologically, but first in creation time)
    if (consultHistory.createdOn) {
      items.push({
        type: 'created',
        timestamp: consultHistory.createdOn,
        data: consultHistory,
      });
    }

    // Sort by timestamp descending (newest first)
    return items.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });
  };

  const renderConsultStatus = (status: ConsultStatus, timestamp: string) => {
    return (
      <React.Fragment key={timestamp}>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Box className="flex mb-4">
            <Box>
              <CustomAvatar id="user-avatar" alt="VA" width={40} height={40}>
                {getInitials(status.modifiedBy)}
              </CustomAvatar>
            </Box>

            <Box className="ml-12 text-align-left">
              <Box className="flex mb-4">
                <CustomTypography id="name" variant="h6" className="pr-4">
                  {formatNameToCamelCase(status.modifiedBy) || ''},
                </CustomTypography>

                <CustomTypography
                  id="consultation"
                  variant="h6"
                  color="textDisabled"
                >
                  {status.action}
                </CustomTypography>
              </Box>

              {!status.fromStatus && status.toStatus && (
                <Box className="flex">
                  <CustomTypography
                    id="name"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    {getFieldLabel(status.updatedColumnName) + ':'}
                  </CustomTypography>

                  <Box className="flex align-items-center">
                    <CustomTypography
                      id="consultation"
                      variant="body3"
                      color="textDisabled"
                      className="pr-12"
                    >
                      {formatFieldValue(status.toStatus)}
                    </CustomTypography>
                  </Box>
                </Box>
              )}

              {status.fromStatus && status.toStatus && (
                <Box className="flex">
                  <CustomTypography
                    id="name"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    {getFieldLabel(status.updatedColumnName) + ':'}
                  </CustomTypography>

                  <Box className="flex align-items-center">
                    <CustomTypography
                      id="consultation"
                      variant="body3"
                      color="textDisabled"
                      className="pr-12"
                    >
                      {formatFieldValue(status.fromStatus)}
                    </CustomTypography>

                    <RightArrow />

                    <CustomTypography
                      id="consultation"
                      variant="body3"
                      color="textDisabled"
                      className="pl-12"
                    >
                      {formatFieldValue(status.toStatus)}
                    </CustomTypography>
                  </Box>
                </Box>
              )}

              {status.previousProvider && (
                <Box className="flex">
                  <CustomTypography
                    id="name"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    Previous Provider:
                  </CustomTypography>
                  <CustomTypography
                    id="consultation"
                    variant="body3"
                    color="textDisabled"
                  >
                    {status.previousProvider}
                  </CustomTypography>
                </Box>
              )}

              {status.waitTime && (
                <Box className="flex">
                  <CustomTypography
                    id="name"
                    color="primary"
                    variant="body3"
                    className="pr-4"
                  >
                    {status?.fromStatus?.toLowerCase() === 'on hold'
                      ? 'On Hold Waiting Time:'
                      : 'Waiting Time:'}
                  </CustomTypography>

                  <Box className="flex align-items-center">
                    <CustomTypography
                      id="consultation"
                      variant="body3"
                      color="textDisabled"
                      className="pr-12"
                    >
                      {status.waitTime}
                    </CustomTypography>
                  </Box>
                </Box>
              )}

              <Box className="flex align-items-center mt-6">
                <Box className="portal-name">{status?.portal || ''}</Box>
                <CustomTypography
                  id="date"
                  variant="body3"
                  color="textDisabled"
                >
                  {formatFieldValue(status.modifiedOn)}
                </CustomTypography>
              </Box>
            </Box>
          </Box>
        </Grid2>

        <Grid2
          className="mt-16 mb-16"
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <CustomDivider />
        </Grid2>
      </React.Fragment>
    );
  };

  const renderHistoryItem = (item: HistoryItem) => {
    switch (item.type) {
      case 'canceled':
        return renderConsultCanceled(item.data);
      case 'completed':
        return renderConsultCompleted(item.data);
      case 'others':
        return renderConsultOtherActions(item.data);
      case 'onHold':
        return renderConsultOnHold(item.data);
      case 'status':
        return renderConsultStatus(item.data, item.timestamp);
      case 'created':
        return renderConsultCreated();
      default:
        return null;
    }
  };

  const historyItems = getAllHistoryItems();

  return (
    <Box>
      {consultHistory ? (
        <Grid2 container>
          {historyItems.map((item, index) => (
            <React.Fragment key={`${item.type}-${item.timestamp}-${index}`}>
              {renderHistoryItem(item)}
            </React.Fragment>
          ))}
        </Grid2>
      ) : (
        <Box>
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Box className="text-align-center">
                No consultation history found
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      )}
    </Box>
  );
};

export default ConsultHistory;
